// ==========================================================================
// Author   : Aneesha Ravi
// Date     : 2026-09-16
// Purpose  : Porter Service - Order Routing screen. Shows unassigned/active
//            porter orders on the left; the right panel lets the admin
//            assign a delivery partner or update the order's status.
// ==========================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  FaTruck, FaStar, FaUser, FaStore, FaCheck, FaChevronRight, FaChevronDown, FaSyncAlt,
  FaBuilding, FaTags, FaCheckCircle, FaCalendarAlt, FaEnvelope, FaPhoneAlt, FaWhatsapp,
  FaMapMarkerAlt, FaClock, FaMotorcycle, FaCar, FaShuttleVan, FaSearch
} from 'react-icons/fa';
import AdminService from '../services/adminService';
import '../css/admin/OrderRouting.css';

// AdminService.adminApiCall() has no request timeout, so a slow backend
// leaves the "Updating..." button stuck forever. Race calls against a local
// timeout so the admin gets an error instead of a frozen button.
const withTimeout = (promise, ms = 20000) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('The server took too long to respond. Please try again.')), ms)
    )
  ]);

// The backend's default/placeholder business_id, present on orders that were
// never actually routed to a vendor - never counts as a real assignment.
const DEFAULT_BUSINESS_ID = 'KIR1502920260601090221';

const OrderRouting = () => {
  // Orders shown in the left-hand list
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  // "Unassigned" only vs "All active" (unassigned + already assigned) porter orders
  const [orderListTab, setOrderListTab] = useState('unassigned');
  const [allPorterOrders, setAllPorterOrders] = useState([]);

  // "Completed" tab - fetched from its own dedicated endpoint (paginated,
  // loaded one page at a time), not from allPorterOrders above.
  const [completedOrders, setCompletedOrders] = useState([]);
  const [completedPage, setCompletedPage] = useState(1);
  const [hasMoreCompleted, setHasMoreCompleted] = useState(false);
  const [loadingCompleted, setLoadingCompleted] = useState(false);
  const [completedError, setCompletedError] = useState('');

  // Filters the left-hand list only, by order # or customer name. Purely a
  // display filter - it doesn't touch orders/allPorterOrders or which order
  // is selected, so nothing else on this screen is affected by it.
  const [orderSearchQuery, setOrderSearchQuery] = useState('');

  // Which order is currently open in the detail panel on the right
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  // null until the admin picks one - "delivery_partner" (assign to our own
  // partner) or "vendor" (not wired to a real API yet). Starting empty keeps
  // the status grid/partner list hidden until the admin actually asks for it.
  const [fulfilmentMode, setFulfilmentMode] = useState(null);

  // Spins the page-level refresh button while reloading everything - the
  // order list, delivery partners, and the currently open order's details
  const [isRefreshingPage, setIsRefreshingPage] = useState(false);

  // Bumped on every refresh so the order-details effect below re-runs even
  // when the selected order itself hasn't changed
  const [detailsRefreshKey, setDetailsRefreshKey] = useState(0);

  // The order's own business's delivery partners (fetched fresh each time
  // RIDER_ACCEPTED is picked, scoped by business_id + vehicle_type - same
  // API used for "Assign to a Vendor")
  const [deliveryPartners, setDeliveryPartners] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [partnersError, setPartnersError] = useState('');
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);

  // The delivery partner list opens in its own dialog instead of showing
  // inline on the page, once RIDER_ACCEPTED is picked in the status grid.
  const [showPartnerDialog, setShowPartnerDialog] = useState(false);

  // Registered vendors/businesses shown under "Assign to a Vendor"
  const [vendors, setVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [vendorsError, setVendorsError] = useState('');
  const [vendorsLoaded, setVendorsLoaded] = useState(false);

  // Vendor shown in the "View Details" dialog - just the row's own data,
  // no extra API call needed since the list already has everything
  const [selectedVendor, setSelectedVendor] = useState(null);

  // The dialog has two stages: 'confirm' (business details + a plain
  // "Assign [vendor] to this order" prompt) then 'status' (order status
  // stepper + that vendor's delivery partners), shown after "Assign Vendor".
  const [vendorDialogStage, setVendorDialogStage] = useState('confirm');

  // Which of the vendor's own delivery partners is picked in that dialog
  const [selectedVendorPartnerId, setSelectedVendorPartnerId] = useState(null);

  // Status picked in the vendor dialog's own status grid - same statuses as
  // the main "Assign to our delivery partner" panel. The partner list only
  // shows up once RIDER_ACCEPTED is picked here, same as that panel too.
  const [selectedVendorStatus, setSelectedVendorStatus] = useState('');
  const [vendorAssignError, setVendorAssignError] = useState('');
  const [isAssigningVendorOrder, setIsAssigningVendorOrder] = useState(false);

  // The vendor's own delivery partners, fetched fresh each time "View
  // Details" is opened for a vendor (AdminService.getPorterVendorDeliveryPartners)
  const [vendorPartners, setVendorPartners] = useState([]);
  const [loadingVendorPartners, setLoadingVendorPartners] = useState(false);
  const [vendorPartnersError, setVendorPartnersError] = useState('');

  // Pickup/drop location for the order currently open in the detail panel.
  // Fetched only for that one order (not the whole list) using the same
  // per-order details API Order Management uses when you click "View".
  const [selectedOrderRoute, setSelectedOrderRoute] = useState(null);
  const [loadingRoute, setLoadingRoute] = useState(false);

  // Items in the order currently open in the detail panel (name, parcel size,
  // parcel weight), fetched from the same per-order details call as the
  // pickup/drop route.
  const [selectedOrderItems, setSelectedOrderItems] = useState([]);

  // Price breakdown (subtotal, tax, delivery/parcel charges, total) for the
  // order open in the panel, from the same per-order details call.
  const [selectedOrderSummary, setSelectedOrderSummary] = useState(null);

  // Payment status/source for the order open in the panel, from the same
  // per-order details call - used as a fallback when the order-list item
  // itself (e.g. from the Completed tab's own endpoint) doesn't carry these.
  const [selectedOrderPayment, setSelectedOrderPayment] = useState(null);

  // Whether the fare's price breakdown (subtotal/tax/total) is expanded
  const [showFareBreakdown, setShowFareBreakdown] = useState(false);

  // Business and delivery partner details for the order open in the panel,
  // from the same per-order details call as the pickup/drop route.
  const [selectedOrderBusiness, setSelectedOrderBusiness] = useState(null);
  const [selectedOrderDeliveryPartner, setSelectedOrderDeliveryPartner] = useState(null);

  // The vehicle type the customer requested for this order (order.porter_details.vehicle_type) -
  // available even before a delivery partner is assigned, unlike delivery_partner.vehicle_type
  const [selectedOrderVehicleType, setSelectedOrderVehicleType] = useState(null);

  // Receiver details (name/phone/email) for the order open in the panel -
  // who the parcel is actually being delivered to, from order.porter_details.
  const [selectedOrderReceiver, setSelectedOrderReceiver] = useState(null);

  // Status list/API/logic - same as the "Update" button in Order Management,
  // shown inline under "Assign to our delivery partner".
  const [statusToApply, setStatusToApply] = useState('');
  const [statusUpdateError, setStatusUpdateError] = useState('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Reason typed by the admin when re-assigning an order's delivery partner
  // (the order already has one) - required by the reassign-rider API.
  const [reassignReason, setReassignReason] = useState('');

  // Exact, fixed set of statuses shown in the stepper (both "Assign to our
  // delivery partner" and "Assign to a vendor"), in this display order.
  const ALLOWED_STATUSES = [
    'PENDING', 'SEARCHING_RIDER', 'RIDER_ASSIGNED', 'RIDER_ACCEPTED',
    'ON_THE_WAY_TO_PICKUP', 'AT_PICKUP', 'PICKED_UP', 'IN_TRANSIT', 'AT_DELIVERY', 'DELIVERED'
  ];

  // Load "Porter Delivery" orders using the same API as Order Management,
  // then split them into "unassigned" and "all active" for the two tabs.
  const fetchPorterOrders = useCallback(async () => {
    try {
      setLoadingOrders(true);
      setErrorMessage('');

      // getAllOrders paginates across ALL order types (not just porter), so a
      // single page-1 call can push older porter orders off the page once
      // enough newer orders of any type exist - fetch every page, same as
      // Order Management does, so nothing is silently missed.
      const firstResponse = await AdminService.getAllOrders({ page: 1, limit: 200 });
      let allOrders = [...(firstResponse?.orders || firstResponse?.results || [])];
      const totalPages = firstResponse?.pagination?.total_pages || firstResponse?.pages || 1;

      if (totalPages > 1) {
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
        const remainingResponses = await Promise.all(
          remainingPages.map((page) => AdminService.getAllOrders({ page, limit: 200 }))
        );
        remainingResponses.forEach((response) => {
          allOrders = allOrders.concat(response?.orders || response?.results || []);
        });
      }

      const porterOrders = allOrders.filter((order) =>
        String(order.order_type || '').toLowerCase().includes('porter')
      );

      setAllPorterOrders(porterOrders);
    } catch (error) {
      console.error('Failed to load orders for routing:', error);
      setErrorMessage('Could not load orders. Please try again.');
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  // Load the "Completed" tab from its dedicated endpoint. That endpoint
  // returns completed orders across every business type, not just porter, so
  // results are filtered down to porter orders (order_number starting with
  // "PRT-"). A single page can come back with very few (or zero) porter
  // orders even though more pages exist, so this keeps fetching subsequent
  // pages automatically until it collects a full batch (20) of porter orders
  // or genuinely runs out of pages (capped at 10 fetches per call, so a "few
  // porter orders left" case can't spin forever) - so "Load more" reliably
  // means there's actually more to show.
  // append=true adds to the existing list ("Load more"); false replaces it
  // (first load / refresh).
  const fetchCompletedOrders = useCallback(async (page, append) => {
    const MAX_PAGES_PER_CALL = 10;
    const TARGET_BATCH_SIZE = 20;
    try {
      setLoadingCompleted(true);
      setCompletedError('');

      let currentPage = page;
      let found = [];
      let hasMore = false;

      for (let attempt = 0; attempt < MAX_PAGES_PER_CALL; attempt += 1) {
        const response = await AdminService.getPorterCompletedOrders(currentPage, 20);
        const results = response?.data?.results || [];
        found = found.concat(
          results.filter((order) => String(order.order_number || '').toUpperCase().startsWith('PRT-'))
        );
        hasMore = Boolean(response?.data?.next);

        if (found.length >= TARGET_BATCH_SIZE || !hasMore) break;
        currentPage += 1;
      }

      // Only claim there's more to load when a full batch was actually found -
      // if 10 pages of searching turned up only a handful, further pages are
      // almost certainly just as sparse, so "Load more" is hidden instead of
      // inviting more clicks that won't add much.
      const reallyHasMore = hasMore && found.length >= TARGET_BATCH_SIZE;

      setCompletedOrders((prev) => (append ? [...prev, ...found] : found));
      setCompletedPage(currentPage);
      setHasMoreCompleted(reallyHasMore);
    } catch (error) {
      console.error('Failed to load completed orders:', error);
      setCompletedError('Could not load completed orders. Please try again.');
    } finally {
      setLoadingCompleted(false);
    }
  }, []);

  // Load delivery partners for "Assign to our delivery partner" - same API as
  // "Assign to a Vendor" but without a business_id, since it's now optional
  // and this flow isn't scoped to any one business. Only vehicle_type is sent.
  const fetchOwnDeliveryPartners = useCallback(async () => {
    try {
      setLoadingPartners(true);
      setPartnersError('');
      const vehicleType = selectedOrderVehicleType || 'Bike';
      const response = await AdminService.getPorterVendorDeliveryPartners(undefined, vehicleType);
      setDeliveryPartners(response?.data || []);
    } catch (error) {
      console.error('Failed to load delivery partners:', error);
      setPartnersError(`Could not load delivery partners: ${error.message}`);
      setDeliveryPartners([]);
    } finally {
      setLoadingPartners(false);
    }
  }, [selectedOrderVehicleType]);

  // Load registered vendors/businesses (only once, the first time the admin opens "Assign to a Vendor")
  const fetchVendors = useCallback(async () => {
    try {
      setLoadingVendors(true);
      setVendorsError('');
      const response = await AdminService.getPorterBusinessDetails();
      setVendors(response?.data || []);
      setVendorsLoaded(true);
    } catch (error) {
      console.error('Failed to load vendors:', error);
      setVendorsError(error.message || 'Could not load vendors right now. Please try again later.');
    } finally {
      setLoadingVendors(false);
    }
  }, []);

  useEffect(() => {
    if (fulfilmentMode === 'vendor' && !vendorsLoaded) {
      fetchVendors();
    }
  }, [fulfilmentMode, vendorsLoaded, fetchVendors]);

  useEffect(() => {
    fetchPorterOrders();
  }, [fetchPorterOrders]);

  // Reload the Completed tab's first page every time it's opened, so newly
  // delivered/cancelled orders show up without needing a manual page refresh.
  useEffect(() => {
    if (orderListTab === 'completed') {
      fetchCompletedOrders(1, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderListTab]);

  // Completed orders come from their own endpoint, in a different shape than
  // allPorterOrders (order_id instead of id, drop_address, no business_name) -
  // map them to the fields the rest of this screen already expects.
  const mappedCompletedOrders = completedOrders.map((order) => ({
    id: order.order_id,
    order_id: order.order_id,
    order_number: order.order_number,
    status: order.status,
    customer_name: order.customer_name,
    delivery_address: order.drop_address,
    vehicle_type: order.vehicle_type,
    created_at: order.created_at,
    order_type: 'porter delivery'
  }));

  // business_id is present on every order (assigned or not), so it can't be used
  // to detect routing. Only PENDING/SEARCHING_RIDER are "not yet routed" -
  // anything past that (RIDER_ASSIGNED onwards), or a delivery partner already
  // on the order, counts as assigned and moves it into "All active".
  const PRE_ROUTING_STATUSES = ['PENDING', 'SEARCHING_RIDER'];
  const isOrderAssigned = (order) => {
    if (order.delivery_partner_name) return true;
    if (order.business_id && order.business_id !== DEFAULT_BUSINESS_ID) return true;
    const status = String(order.status || '').toUpperCase();
    return Boolean(status) && !PRE_ROUTING_STATUSES.includes(status);
  };

  // Delivered/completed/cancelled/rejected orders move out of Unassigned and
  // All active into their own Completed tab, instead of disappearing entirely.
  const COMPLETED_STATUSES = [
    'DELIVERED', 'COMPLETED', 'CANCELLED', 'REJECTED',
    'CANCELLED_BY_CUSTOMER', 'CANCELLED_BY_RIDER'
  ];
  const isOrderCompleted = (order) => COMPLETED_STATUSES.includes(String(order.status || '').toUpperCase());

  // The Completed tab shows the dedicated completed-orders endpoint's results,
  // plus any terminal-status order already in the general order list that
  // endpoint hasn't returned yet (e.g. indexing lag) - deduped by order id, so
  // a delivered/cancelled order is never simply missing from every tab.
  const completedFromOrderList = allPorterOrders.filter(isOrderCompleted);
  const visibleCompletedOrders = [
    ...mappedCompletedOrders,
    ...completedFromOrderList.filter(
      (order) => !mappedCompletedOrders.some((c) => c.id === (order.id || order.order_id))
    )
  ];

  // Apply the "Unassigned / All active / Completed" tab filter and keep the visible list in sync
  useEffect(() => {
    const visibleOrders =
      orderListTab === 'unassigned'
        ? allPorterOrders.filter((order) => !isOrderCompleted(order) && !isOrderAssigned(order))
        : orderListTab === 'completed'
          ? visibleCompletedOrders
          : allPorterOrders.filter((order) => !isOrderCompleted(order) && isOrderAssigned(order));

    setOrders(visibleOrders);

    // Keep a valid order selected. If a different order takes the old one's
    // place (e.g. it just got assigned), reset the fulfilment selections so
    // the new order doesn't inherit stale status/partner choices.
    if (visibleOrders.length === 0) {
      setSelectedOrderId(null);
      setFulfilmentMode(null);
      setStatusToApply('');
      setSelectedPartnerId(null);
    } else if (!visibleOrders.some((order) => (order.id || order.order_id) === selectedOrderId)) {
      setSelectedOrderId(visibleOrders[0].id || visibleOrders[0].order_id);
      setFulfilmentMode(null);
      setStatusToApply('');
      setSelectedPartnerId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPorterOrders, completedOrders, orderListTab]);


  // Left-hand list after the order #/customer-name search filter. Only
  // affects what's rendered in the list below - selection and the right
  // panel still use the full "orders" array, unaffected by this filter.
  const searchedOrders = (() => {
    const query = orderSearchQuery.trim().toLowerCase();
    if (!query) return orders;
    return orders.filter((order) => {
      const orderId = String(order.id || order.order_id || '').toLowerCase();
      const customerName = String(order.customer_name || '').toLowerCase();
      return orderId.includes(query) || customerName.includes(query);
    });
  })();

  const selectedOrder = orders.find((order) => (order.id || order.order_id) === selectedOrderId);
  const selectedPartner = deliveryPartners.find((partner) => partner.partner_id === selectedPartnerId);
  const selectedVendorPartner = vendorPartners.find((partner) => partner.partner_id === selectedVendorPartnerId);

  // Purely decorative icon per stepper step - same status values as everywhere else
  const stepIcon = (status) => {
    const upper = String(status).toUpperCase();
    if (upper === 'RIDER_ACCEPTED') return <FaUser />;
    if (upper === 'ON_THE_WAY_TO_PICKUP') return <FaClock />;
    if (upper === 'AT_PICKUP' || upper === 'AT_DELIVERY') return <FaMapMarkerAlt />;
    if (upper === 'PICKED_UP' || upper === 'IN_TRANSIT') return <FaTruck />;
    return <FaCheck />;
  };

  // Small icon next to a delivery partner's vehicle type tag
  const vehicleIcon = (vehicleType) => {
    const upper = String(vehicleType || '').toUpperCase();
    if (upper.includes('BIKE') || upper.includes('MOTORCYCLE') || upper.includes('SCOOTY') || upper.includes('SCOOTER')) return <FaMotorcycle />;
    if (upper.includes('TRUCK')) return <FaTruck />;
    if (upper.includes('AUTO') || upper.includes('3-WHEELER') || upper.includes('3 WHEELER')) return <FaShuttleVan />;
    if (upper.includes('CAR')) return <FaCar />;
    return <FaTruck />;
  };

  // Only show these cards when there's actually something to show
  const hasBusinessDetails = Boolean(selectedOrderBusiness?.business_name || selectedOrder?.business_name);
  const hasDeliveryPartnerDetails = Boolean(selectedOrder?.delivery_partner_name || selectedOrderDeliveryPartner);

  // Can't route an order for delivery when payment hasn't gone through -
  // failed or missing payment status blocks both fulfilment options.
  const effectivePaymentStatus = String(
    selectedOrder?.payment_status || selectedOrderPayment?.status || ''
  ).toLowerCase();
  const isPaymentBlocked = !effectivePaymentStatus || effectivePaymentStatus === 'failed';

  // Order placed date/time, formatted for display
  const formatOrderDate = (createdAt) => {
    if (!createdAt) return 'N/A';
    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString();
  };

  // How long ago the order was placed, e.g. "3m", "2h 10m", "1d 4h"
  const formatWaitingTime = (createdAt) => {
    if (!createdAt) return 'N/A';
    const elapsedMs = Date.now() - new Date(createdAt).getTime();
    if (Number.isNaN(elapsedMs) || elapsedMs < 0) return 'N/A';

    const totalMinutes = Math.floor(elapsedMs / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const isCashOnDelivery = (order) =>
    String(order?.payment_source || order?.payment_status || '').toLowerCase().includes('cod');

  // Address field can be a plain string or an address object - show plain text
  const formatAddress = (address) => {
    if (!address) return null;
    if (typeof address === 'string') return address;
    return address.full_address || address.address || null;
  };

  // Reset the fulfilment selections whenever a different order is opened
  const handleSelectOrder = (order) => {
    setSelectedOrderId(order.id || order.order_id);
    setSelectedPartnerId(null);
    setStatusToApply('');
    setStatusUpdateError('');
    setFulfilmentMode(null);
    setShowPartnerDialog(false);
    setDeliveryPartners([]);
    setPartnersError('');
    setShowFareBreakdown(false);
    setReassignReason('');
  };


  // The order's already-assigned vendor, if any - business_id is set on the
  // order (list and details) once "Assign Vendor" has succeeded for it.
  const rawAssignedVendorId = selectedOrder?.business_id || selectedOrderBusiness?.business_id || null;
  const assignedVendorId = rawAssignedVendorId === DEFAULT_BUSINESS_ID ? null : rawAssignedVendorId;

  // A delivery partner is only "ours" if the order wasn't routed to a vendor -
  // otherwise that partner belongs to the vendor, not us directly.
  const isOwnPartnerAssigned = hasDeliveryPartnerDetails && !assignedVendorId;

  // KIR1502920260601090221 is a default/test vendor that should never be
  // offered as a real assignment option in the "Assign to a Vendor" list.
  const visibleVendors = vendors.filter((vendor) => vendor.business_id !== DEFAULT_BUSINESS_ID);

  // Once an order is committed to one fulfilment path (a vendor, or our own
  // delivery partner directly), status updates only happen through that
  // path's own statuses - drop out of the other one if it was left open from
  // before that commitment happened.
  useEffect(() => {
    if (assignedVendorId && fulfilmentMode === 'delivery_partner') {
      setFulfilmentMode(null);
    }
    if (isOwnPartnerAssigned && fulfilmentMode === 'vendor') {
      setFulfilmentMode(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedVendorId, isOwnPartnerAssigned]);

  // Opens the vendor details dialog and loads that vendor's own delivery
  // partners. vehicle_type comes from the order's already-assigned delivery
  // partner if it has one; if not, it's left out of the request rather than guessed.
  // If this order is already assigned to the vendor being viewed, the confirm/
  // "Assign Vendor" step is skipped - go straight to the status step.
  const handleViewVendor = async (vendor) => {
    const alreadyAssigned = assignedVendorId && vendor.business_id === assignedVendorId;

    setSelectedVendor(vendor);
    setSelectedVendorPartnerId(null);
    setSelectedVendorStatus('');
    setVendorAssignError('');
    setVendorPartners([]);
    setVendorPartnersError('');
    setVendorDialogStage(alreadyAssigned ? 'status' : 'confirm');
    setReassignReason('');

    try {
      setLoadingVendorPartners(true);
      // Real requested vehicle type from order.porter_details.vehicle_type.
      // Falls back to the assigned delivery partner's vehicle, then "Bike"
      // as a last resort, since the API requires this field.
      const vehicleType = selectedOrderVehicleType || selectedOrderDeliveryPartner?.vehicle_type || 'Bike';
      const response = await AdminService.getPorterVendorDeliveryPartners(vendor.business_id, vehicleType);
      setVendorPartners(response?.data || []);
    } catch (error) {
      console.error('Failed to load vendor delivery partners:', error);
      setVendorPartnersError(`Could not load delivery partners: ${error.message}`);
    } finally {
      setLoadingVendorPartners(false);
    }
  };

  // "Assign Vendor" on the confirm step - links the order to the vendor
  // business, then moves on to the status/delivery partner step.
  const handleConfirmVendorAssignment = async () => {
    if (!selectedOrder || !selectedVendor) return;
    const orderId = selectedOrder.id || selectedOrder.order_id;

    setIsAssigningVendorOrder(true);
    setVendorAssignError('');
    try {
      await withTimeout(AdminService.assignPorterOrderToVendor(orderId, selectedVendor.business_id));

      // Patch this order's business_id locally right away, so it moves out of
      // "Unassigned" into "All active" immediately - the list API can lag
      // behind the write, so waiting on a refetch alone isn't reliable here.
      setAllPorterOrders((prev) =>
        prev.map((order) =>
          (order.id || order.order_id) === orderId
            ? { ...order, business_id: selectedVendor.business_id }
            : order
        )
      );

      // Refresh this order's own details (badge, business details, etc.) -
      // not the whole order list, since that can still return the pre-update
      // business_id for a bit and would overwrite the patch above with it.
      setDetailsRefreshKey((key) => key + 1);
      setVendorDialogStage('status');
    } catch (error) {
      setVendorAssignError(`Could not assign vendor: ${error.message}`);
    } finally {
      setIsAssigningVendorOrder(false);
    }
  };

  // Selecting a status in the grid opens the partner dialog automatically
  // when that status is RIDER_ACCEPTED - the dialog is the only place the
  // partner list is shown, it never sits inline on the page.
  const handleSelectStatus = (status) => {
    setStatusToApply(status);
    setStatusUpdateError('');
    if (isAssignmentStatus(status)) {
      setShowPartnerDialog(true);
      fetchOwnDeliveryPartners();
    }
  };

  // Fetch pickup/drop location for the order shown in the detail panel.
  // Only the per-order details API returns these; the list API doesn't.
  useEffect(() => {
    if (!selectedOrder) {
      setSelectedOrderRoute(null);
      setSelectedOrderItems([]);
      setSelectedOrderBusiness(null);
      setSelectedOrderDeliveryPartner(null);
      setSelectedOrderVehicleType(null);
      setSelectedOrderSummary(null);
      setSelectedOrderPayment(null);
      setSelectedOrderReceiver(null);
      return;
    }

    let isCancelled = false;
    const orderId = selectedOrder.id || selectedOrder.order_id;

    (async () => {
      try {
        setLoadingRoute(true);
        const details = await AdminService.getOrderDetails(orderId, selectedOrder.order_system || 'standard', selectedOrder);
        const orderDetails = details?.data?.order || details?.data || details?.order || {};
        if (isCancelled) return;

        setSelectedOrderRoute({
          pickup: formatAddress(orderDetails.pickup_address),
          drop: formatAddress(orderDetails.delivery_address) || formatAddress(selectedOrder.delivery_address)
        });
        setSelectedOrderItems(orderDetails.items || []);
        setSelectedOrderBusiness(orderDetails.business_details || null);
        setSelectedOrderDeliveryPartner(orderDetails.delivery_partner || null);
        setSelectedOrderVehicleType(orderDetails.porter_details?.vehicle_type || null);
        setSelectedOrderSummary(orderDetails.summary || null);
        setSelectedOrderPayment({
          status: orderDetails.payment_status || null,
          source: orderDetails.payment_details?.payment_source || null
        });
        setSelectedOrderReceiver(
          orderDetails.porter_details?.receiver_name ||
            orderDetails.porter_details?.receiver_phone ||
            orderDetails.porter_details?.receiver_email
            ? {
              name: orderDetails.porter_details?.receiver_name || null,
              phone: orderDetails.porter_details?.receiver_phone || null,
              email: orderDetails.porter_details?.receiver_email || null
            }
            : null
        );
      } catch (error) {
        console.error('Failed to load details for order', orderId, error);
        if (!isCancelled) {
          setSelectedOrderRoute(null);
          setSelectedOrderItems([]);
          setSelectedOrderBusiness(null);
          setSelectedOrderDeliveryPartner(null);
          setSelectedOrderVehicleType(null);
          setSelectedOrderSummary(null);
          setSelectedOrderPayment(null);
          setSelectedOrderReceiver(null);
        }
      } finally {
        if (!isCancelled) setLoadingRoute(false);
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [selectedOrderId, detailsRefreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fixed status stepper - exactly ALLOWED_STATUSES, in that order.
  const getStatusGridOptions = () => {
    if (!selectedOrder) return [];
    return ALLOWED_STATUSES;
  };

  const isAssignmentStatus = (status) =>
    status === 'assigned' || String(status).toUpperCase() === 'RIDER_ASSIGNED';

  // Display-only label - the actual value sent to the API is unchanged.
  // Used for stepper step labels ("Assigning Order" is the step name there).
  const formatStatusLabel = (status) => {
    const upper = String(status).toUpperCase();
    if (upper === 'RIDER_ACCEPTED') return 'RIDER ACCEPTED';
    if (upper === 'RIDER_ASSIGNED' || upper === 'RIDER_ALLOCATED') return 'ASSIGNING ORDER';
    return upper.replace(/_/g, ' ');
  };

  // Same as formatStatusLabel, but for showing an order's CURRENT status
  // (e.g. the Order Status tile) - once RIDER_ASSIGNED/RIDER_ALLOCATED is the
  // order's actual current status, it reads as "Order Assigned" there.
  const formatCurrentStatusLabel = (status) => {
    const upper = String(status).toUpperCase();
    if (upper === 'RIDER_ASSIGNED' || upper === 'RIDER_ALLOCATED') return 'ORDER ASSIGNED';
    return formatStatusLabel(status);
  };

  // Same logic as Order Management's "Update" button: assignment statuses
  // use the assign API, everything else uses the plain status update API.
  const handleApplyStatus = async () => {
    if (!statusToApply) {
      setStatusUpdateError('Please select a status');
      return;
    }

    if (isAssignmentStatus(statusToApply) && !selectedPartner) {
      setStatusUpdateError('Please select a delivery partner below first');
      return;
    }

    const isReassigning = isAssignmentStatus(statusToApply) && hasDeliveryPartnerDetails;
    if (isReassigning && !reassignReason.trim()) {
      setStatusUpdateError('Please enter a reason for reassigning the delivery partner');
      return;
    }

    try {
      setIsUpdatingStatus(true);
      setStatusUpdateError('');
      const orderId = selectedOrder.id || selectedOrder.order_id;
      const orderSystem = selectedOrder.order_system || 'standard';

      if (isReassigning) {
        await withTimeout(AdminService.reassignPorterRider(orderId, selectedPartner.partner_id, reassignReason.trim()));
      } else if (isAssignmentStatus(statusToApply)) {
        await withTimeout(AdminService.assignPorterRider(orderId, selectedPartner.partner_id));
      } else {
        await withTimeout(AdminService.updateOrderStatus(orderId, statusToApply, orderSystem));
      }

      await fetchPorterOrders();
      setDetailsRefreshKey((key) => key + 1);
      setStatusToApply('');
      setSelectedPartnerId(null);
      setReassignReason('');
    } catch (error) {
      console.error('Failed to update order status:', error);
      setStatusUpdateError(error.message || 'Could not update status. Please try again.');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Same exact logic as handleApplyStatus above, just scoped to the vendor
  // dialog's own status/partner selections - assignment statuses use the
  // assign API (+ porter accept notification), everything else uses the
  // plain status update API.
  const handleAssignToVendor = async () => {
    if (!selectedVendorStatus) {
      setVendorAssignError('Please select a status');
      return;
    }

    if (isAssignmentStatus(selectedVendorStatus) && !selectedVendorPartner) {
      setVendorAssignError('Please select a delivery partner below first');
      return;
    }

    const isReassigning = isAssignmentStatus(selectedVendorStatus) && hasDeliveryPartnerDetails;
    if (isReassigning && !reassignReason.trim()) {
      setVendorAssignError('Please enter a reason for reassigning the delivery partner');
      return;
    }

    try {
      setIsAssigningVendorOrder(true);
      setVendorAssignError('');
      const orderId = selectedOrder.id || selectedOrder.order_id;
      const orderSystem = selectedOrder.order_system || 'standard';

      if (isReassigning) {
        await withTimeout(AdminService.reassignPorterRider(orderId, selectedVendorPartner.partner_id, reassignReason.trim()));
      } else if (isAssignmentStatus(selectedVendorStatus)) {
        await withTimeout(AdminService.assignPorterRider(orderId, selectedVendorPartner.partner_id));
      } else {
        await withTimeout(AdminService.updateOrderStatus(orderId, selectedVendorStatus, orderSystem));
      }

      await fetchPorterOrders();
      setDetailsRefreshKey((key) => key + 1);
      setSelectedVendor(null);
      setSelectedVendorPartnerId(null);
      setReassignReason('');
      setSelectedVendorStatus('');
    } catch (error) {
      console.error('Failed to assign order:', error);
      setVendorAssignError(error.message || 'Could not assign order. Please try again.');
    } finally {
      setIsAssigningVendorOrder(false);
    }
  };

  // Reload everything - order list, vendors (if already loaded), and the
  // open order's details - so nothing shown is stale.
  const handleRefreshPage = async () => {
    try {
      setIsRefreshingPage(true);
      const refreshCalls = [fetchPorterOrders()];
      if (vendorsLoaded) refreshCalls.push(fetchVendors());
      if (orderListTab === 'completed') refreshCalls.push(fetchCompletedOrders(1, false));
      await Promise.all(refreshCalls);
      setDetailsRefreshKey((key) => key + 1);
    } finally {
      setIsRefreshingPage(false);
    }
  };

  return (
    <div className="order-routing-page">
      {isRefreshingPage && (
        <div className="order-routing-page-refresh-overlay">
          <FaSyncAlt className="spinning" />
          <span>Refreshing...</span>
        </div>
      )}

      {/* LEFT: list of porter orders */}
      <div className="order-routing-list-panel">
        <div className="order-routing-list-header">
          <div className="order-routing-list-header-top">
            <h2>Order List</h2>
            <button
              type="button"
              className={`order-routing-refresh-btn ${isRefreshingPage ? 'spinning' : ''}`}
              onClick={handleRefreshPage}
              disabled={isRefreshingPage}
              title="Refresh"
            >
              <FaSyncAlt />
            </button>
          </div>
          <p>Customer orders waiting to be routed</p>
        </div>

        <div className="order-routing-tabs">
          <button
            className={`order-routing-tab ${orderListTab === 'unassigned' ? 'active' : ''}`}
            onClick={() => setOrderListTab('unassigned')}
          >
            Unassigned
          </button>
          <button
            className={`order-routing-tab ${orderListTab === 'active' ? 'active' : ''}`}
            onClick={() => setOrderListTab('active')}
          >
            All active
          </button>
          <button
            className={`order-routing-tab ${orderListTab === 'completed' ? 'active' : ''}`}
            onClick={() => setOrderListTab('completed')}
          >
            Completed
          </button>
        </div>

        <div className="order-routing-search">
          <FaSearch className="order-routing-search-icon" />
          <input
            type="text"
            placeholder="Search by order # or customer name"
            value={orderSearchQuery}
            onChange={(e) => setOrderSearchQuery(e.target.value)}
          />
        </div>

        {(orderListTab === 'completed' ? completedError : errorMessage) && (
          <div className="order-routing-error">{orderListTab === 'completed' ? completedError : errorMessage}</div>
        )}

        {(orderListTab === 'completed' ? loadingCompleted && orders.length === 0 : loadingOrders) ? (
          <div className="order-routing-loading">Loading orders...</div>
        ) : orders.length === 0 ? (
          <div className="order-routing-empty">No porter orders to show right now.</div>
        ) : searchedOrders.length === 0 ? (
          <div className="order-routing-empty">No orders match your search.</div>
        ) : (
          <div className="order-routing-order-cards">
            {searchedOrders.map((order) => {
              const orderId = order.id || order.order_id;
              const isSelected = orderId === selectedOrderId;

              return (
                <div
                  key={orderId}
                  className={`order-routing-order-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleSelectOrder(order)}
                >
                  <div className="order-routing-order-card-top">
                    <div className="order-routing-order-card-icon">
                      {vehicleIcon(order.vehicle_type)}
                    </div>
                    <div className="order-routing-order-card-id">{orderId}</div>
                    <span className="order-routing-order-card-time">{formatWaitingTime(order.created_at)}</span>
                  </div>
                  <div className="order-routing-order-card-customer">
                    {order.customer_name || 'N/A'}
                  </div>
                </div>
              );
            })}
            {orderListTab === 'completed' && hasMoreCompleted && (
              <button
                type="button"
                className="order-routing-load-more-btn"
                onClick={() => fetchCompletedOrders(completedPage + 1, true)}
                disabled={loadingCompleted}
              >
                {loadingCompleted ? 'Loading...' : 'Load more'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* RIGHT: details for the selected order */}
      <div className="order-routing-detail-panel">
        {!selectedOrder ? (
          <div className="order-routing-empty order-routing-detail-empty">
            Select an order from the list to view details.
          </div>
        ) : (
          <>
            <div className="order-routing-detail-card">
              <div className="order-routing-detail-card-top">
                <div className="order-routing-detail-card-icon">
                  <FaTruck />
                </div>
                <div className="order-routing-detail-card-body">
                  <div className="order-routing-detail-id">
                    {selectedOrder.id || selectedOrder.order_id}
                  </div>
                  <div className="order-routing-detail-customer">
                    {selectedOrder.customer_name || 'N/A'}
                  </div>
                  <div className="order-routing-detail-subtext">
                    {selectedOrder.order_type || 'Porter Delivery'}
                    {selectedOrder.customer_phone ? ` · ${selectedOrder.customer_phone}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  className="order-routing-detail-fare"
                  disabled={!selectedOrderSummary}
                  onClick={() => setShowFareBreakdown((open) => !open)}
                >
                  <div className="order-routing-detail-fare-amount">
                    &#8377;{Number(selectedOrder.total_amount ?? selectedOrderSummary?.total ?? 0).toFixed(2)}
                    {selectedOrderSummary && (
                      showFareBreakdown ? <FaChevronDown /> : <FaChevronRight />
                    )}
                  </div>
                  <div className="order-routing-detail-fare-label">delivery fare</div>
                </button>
              </div>

              {showFareBreakdown && selectedOrderSummary && (
                <div className="order-routing-price-breakdown">
                  <div className="order-routing-price-row">
                    <span>Subtotal:</span>
                    <strong>&#8377;{Number(selectedOrderSummary.subtotal ?? 0).toFixed(2)}</strong>
                  </div>
                  {Number(selectedOrderSummary.delivery_charges) > 0 && (
                    <div className="order-routing-price-row">
                      <span>Delivery Charges:</span>
                      <strong>&#8377;{Number(selectedOrderSummary.delivery_charges).toFixed(2)}</strong>
                    </div>
                  )}
                  {Number(selectedOrderSummary.parcel_charges) > 0 && (
                    <div className="order-routing-price-row">
                      <span>Parcel Charges:</span>
                      <strong>&#8377;{Number(selectedOrderSummary.parcel_charges).toFixed(2)}</strong>
                    </div>
                  )}
                  <div className="order-routing-price-row">
                    <span>Tax / GST:</span>
                    <strong>&#8377;{Number(selectedOrderSummary.tax ?? 0).toFixed(2)}</strong>
                  </div>
                  <div className="order-routing-price-divider" />
                  <div className="order-routing-price-row total">
                    <span>Total Amount:</span>
                    <strong>&#8377;{Number(selectedOrderSummary.total ?? 0).toFixed(2)}</strong>
                  </div>
                </div>
              )}
            </div>

            <div className="order-routing-route-card">
              <div className="order-routing-info-title">Pickup &amp; Drop</div>
              {loadingRoute ? (
                <div className="order-routing-info-subtext">Loading location...</div>
              ) : (
                <div className="order-routing-route-lines">
                  <div className="order-routing-route-line">
                    <span className="dot pickup" />
                    <span>{selectedOrderRoute?.pickup || 'Pickup location not available'}</span>
                  </div>
                  <div className="order-routing-route-line">
                    <span className="dot drop" />
                    <span>{selectedOrderRoute?.drop || 'Drop location not available'}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="order-routing-info-grid">
              <div className="order-routing-info-card">
                <div className="order-routing-info-title">Parcel</div>
                {loadingRoute ? (
                  <div className="order-routing-info-subtext">Loading...</div>
                ) : selectedOrderItems.length === 0 ? (
                  <div className="order-routing-info-value">{selectedOrder.business_name || 'N/A'}</div>
                ) : (
                  selectedOrderItems.map((item, index) => (
                    <div key={index} className="order-routing-parcel-item">
                      <span className="order-routing-info-value">{item.name || item.item_name || 'N/A'}</span>
                      <span className="order-routing-info-subtext">
                        Size: {item.parcel_size || 'N/A'}
                        {item.parcel_weight ? ` · ${item.parcel_weight} kg` : ''}
                      </span>
                      {item.parcel_description && (
                        <span className="order-routing-info-subtext">Description: {item.parcel_description}</span>
                      )}
                    </div>
                  ))
                )}
                {isCashOnDelivery(selectedOrder) && (
                  <span className="order-routing-cod-pill">
                    COD &#8377;{selectedOrder.total_amount ?? '0.00'}
                  </span>
                )}
              </div>

              <div className="order-routing-info-card">
                <div className="order-routing-info-title">Order Status</div>
                <div className="order-routing-info-value">
                  {assignedVendorId && PRE_ROUTING_STATUSES.includes(String(selectedOrder.status || '').toUpperCase())
                    ? 'ASSIGNED TO VENDOR'
                    : selectedOrder.status
                      ? formatCurrentStatusLabel(selectedOrder.status)
                      : 'N/A'}
                </div>
                <div className="order-routing-info-subtext">{formatOrderDate(selectedOrder.created_at)}</div>
              </div>

              <div className="order-routing-info-card">
                <div className="order-routing-info-title">Payment</div>
                <div className="order-routing-info-value">
                  {selectedOrder.payment_status || selectedOrderPayment?.status || 'N/A'}
                </div>
                <div className="order-routing-info-subtext">
                  {selectedOrder.payment_source || selectedOrder.source || selectedOrderPayment?.source || 'N/A'}
                </div>
              </div>
            </div>

            <div className="order-routing-fulfil-title">How do you want to fulfil this order?</div>
            {isPaymentBlocked && !isOwnPartnerAssigned && !assignedVendorId && (
              <div className="order-routing-error">
                This order can't be routed yet - payment hasn't been completed.
              </div>
            )}
            <div className="order-routing-fulfil-toggle">
              <button
                className={`order-routing-fulfil-option ${fulfilmentMode === 'delivery_partner' ? 'active' : ''} ${isOwnPartnerAssigned ? 'assigned' : ''}`}
                disabled={isOrderCompleted(selectedOrder) || Boolean(assignedVendorId) || (isPaymentBlocked && !isOwnPartnerAssigned)}
                onClick={() => {
                  setFulfilmentMode('delivery_partner');
                  setStatusToApply('');
                  setStatusUpdateError('');
                  setSelectedPartnerId(null);
                }}
              >
                <span className="order-routing-fulfil-icon partner">
                  <FaUser />
                </span>
                <span className="order-routing-fulfil-text">
                  <span className="order-routing-fulfil-option-title">
                    {isOwnPartnerAssigned ? 'Assigned to our delivery partner' : 'Assign to our delivery partner'}
                  </span>
                  {!isOwnPartnerAssigned && (
                    <span className="order-routing-fulfil-option-subtext">Recommended</span>
                  )}
                </span>
                {fulfilmentMode === 'delivery_partner' || isOwnPartnerAssigned ? (
                  <span className="order-routing-fulfil-check"><FaCheck /></span>
                ) : (
                  <span className="order-routing-fulfil-option-arrow"><FaChevronRight /></span>
                )}
              </button>

              <button
                className={`order-routing-fulfil-option ${fulfilmentMode === 'vendor' ? 'active' : ''} ${assignedVendorId ? 'assigned' : ''}`}
                disabled={isOrderCompleted(selectedOrder) || isOwnPartnerAssigned || (isPaymentBlocked && !assignedVendorId)}
                onClick={() => setFulfilmentMode('vendor')}
              >
                <span className="order-routing-fulfil-icon vendor">
                  <FaStore />
                </span>
                <span className="order-routing-fulfil-text">
                  <span className="order-routing-fulfil-option-title">
                    {assignedVendorId ? 'Assigned Vendor' : 'Assign to a vendor'}
                  </span>
                  {vendorsLoaded && !assignedVendorId && (
                    <span className="order-routing-fulfil-option-subtext">{visibleVendors.length} registered</span>
                  )}
                </span>
                {fulfilmentMode === 'vendor' || assignedVendorId ? (
                  <span className="order-routing-fulfil-check"><FaCheck /></span>
                ) : (
                  <span className="order-routing-fulfil-option-arrow"><FaChevronRight /></span>
                )}
              </button>
            </div>

            {fulfilmentMode === 'vendor' && !isOwnPartnerAssigned && (
              <div className="order-routing-vendors-card">
                <div className="order-routing-vendors-header">
                  <span>All vendors</span>
                  <span className="order-routing-vendors-count">{visibleVendors.length} registered</span>
                </div>

                {loadingVendors ? (
                  <div className="order-routing-loading">Loading vendors...</div>
                ) : vendorsError ? (
                  <div className="order-routing-error">{vendorsError}</div>
                ) : visibleVendors.length === 0 ? (
                  <div className="order-routing-empty">No vendors registered yet.</div>
                ) : (
                  <div className="order-routing-vendors-list">
                    {[...visibleVendors]
                      .sort((a, b) => (b.business_id === assignedVendorId) - (a.business_id === assignedVendorId))
                      .map((vendor) => {
                        const isAssigned = assignedVendorId && vendor.business_id === assignedVendorId;
                        return (
                          <div key={vendor.business_id} className={`order-routing-vendor-row ${isAssigned ? 'assigned' : ''}`}>
                            <div className="order-routing-vendor-avatar">
                              {(vendor.business_name || '?').slice(0, 2).toUpperCase()}
                            </div>
                            <div className="order-routing-vendor-info">
                              <div className="order-routing-vendor-name-row">
                                <span className="order-routing-vendor-name">{vendor.business_name}</span>
                                {isAssigned ? (
                                  <span className="order-routing-vendor-status assigned">Assigned to this vendor</span>
                                ) : (
                                  <span className={`order-routing-vendor-status ${vendor.status === 1 ? 'active' : 'inactive'}`}>
                                    {vendor.status === 1 ? 'Active' : 'Inactive'}
                                  </span>
                                )}
                              </div>
                              <div className="order-routing-vendor-meta">
                                {vendor.business_id} &middot; {vendor.business_category || 'N/A'}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="order-routing-view-vendor-btn"
                              onClick={() => handleViewVendor(vendor)}
                            >
                              View Details
                            </button>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            )}

            {fulfilmentMode === 'delivery_partner' && !assignedVendorId && (
              <div className="order-routing-status-panel">
                <div className="order-routing-current-status-pill">
                  {selectedOrder.status ? formatCurrentStatusLabel(selectedOrder.status) : 'N/A'}
                </div>

                {statusUpdateError && <div className="order-routing-error">{statusUpdateError}</div>}

                {(() => {
                  const steps = getStatusGridOptions();
                  const STEP_WIDTH = 84;
                  const currentIndex = steps.findIndex(
                    (s) => String(selectedOrder.status || '').toLowerCase() === String(s).toLowerCase()
                  );
                  const progressIndex = currentIndex === -1 ? 0 : currentIndex;

                  return (
                    <div className="order-routing-main-stepper-track">
                      <span
                        className="order-routing-main-stepper-line-base"
                        style={{ width: `${(steps.length - 1) * STEP_WIDTH}px` }}
                      />
                      <span
                        className="order-routing-main-stepper-line-progress"
                        style={{ width: `${progressIndex * STEP_WIDTH}px` }}
                      />
                      <div className="order-routing-main-stepper">
                        {steps.map((status, index) => {
                          const isCurrent = String(selectedOrder.status || '').toLowerCase() === String(status).toLowerCase();
                          const isPast = currentIndex !== -1 && index < currentIndex;
                          // Only the very next status can be picked - can't skip
                          // ahead past it, and can't go back to an earlier one.
                          const isSkippedAhead = currentIndex !== -1 && index > currentIndex + 1;
                          const isSelected = statusToApply === status;
                          return (
                            <button
                              key={status}
                              type="button"
                              className={`order-routing-main-stepper-step ${isCurrent ? 'current' : ''} ${isPast ? 'completed' : ''} ${isSelected && !isCurrent ? 'selected' : ''}`}
                              disabled={isAssignmentStatus(status) ? false : (isCurrent || isPast || isSkippedAhead)}
                              onClick={() => handleSelectStatus(status)}
                            >
                              <span className="order-routing-main-stepper-circle">
                                {isPast ? <FaCheck /> : stepIcon(status)}
                              </span>
                              <span className="order-routing-main-stepper-label">{formatStatusLabel(status)}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Once RIDER_ACCEPTED is picked above, show who was chosen and let the admin reopen the dialog to change it */}
                {isAssignmentStatus(statusToApply) && (
                  <div className="order-routing-chosen-partner">
                    <span>
                      {selectedPartner
                        ? `Delivery partner: ${selectedPartner.name}`
                        : 'No delivery partner chosen yet'}
                    </span>
                    <button
                      type="button"
                      className="order-routing-choose-partner-btn"
                      onClick={() => setShowPartnerDialog(true)}
                    >
                      {selectedPartner ? 'Change' : 'Choose partner'}
                    </button>
                  </div>
                )}

                <div className="order-routing-footer">
                  <span className="order-routing-footer-hint">
                    {isAssignmentStatus(statusToApply) && !selectedPartner
                      ? 'Select a delivery partner to continue'
                      : ''}
                  </span>
                  <button
                    className="order-routing-assign-order-btn"
                    disabled={isUpdatingStatus || !statusToApply || (isAssignmentStatus(statusToApply) && !selectedPartner)}
                    onClick={handleApplyStatus}
                  >
                    {isUpdatingStatus
                      ? 'Updating...'
                      : isAssignmentStatus(statusToApply)
                        ? 'Assign Order'
                        : 'Update Status'}
                  </button>
                </div>
              </div>
            )}

            {(hasBusinessDetails || hasDeliveryPartnerDetails || selectedOrderReceiver) && (
              <div className="order-routing-secondary-grid">
                {hasBusinessDetails && (
                  <div className="order-routing-detail-section">
                    <div className="order-routing-info-title">Business Details</div>
                    <div className="order-routing-detail-row">
                      <span>Business Name</span>
                      <strong>{selectedOrderBusiness?.business_name || selectedOrder.business_name || 'N/A'}</strong>
                    </div>
                    <div className="order-routing-detail-row">
                      <span>Contact Number</span>
                      <strong>{selectedOrderBusiness?.business_number || 'N/A'}</strong>
                    </div>
                    <div className="order-routing-detail-row">
                      <span>Address</span>
                      <strong>
                        {selectedOrderBusiness?.address || selectedOrderBusiness?.business_address || 'N/A'}
                        {selectedOrderBusiness?.city ? `, ${selectedOrderBusiness.city}` : ''}
                        {selectedOrderBusiness?.pincode ? ` - ${selectedOrderBusiness.pincode}` : ''}
                      </strong>
                    </div>
                  </div>
                )}

                {hasDeliveryPartnerDetails && (
                  <div className="order-routing-detail-section">
                    <div className="order-routing-info-title">Delivery Partner Details</div>
                    <div className="order-routing-detail-row">
                      <span>Name</span>
                      <strong>{selectedOrderDeliveryPartner?.name || selectedOrder.delivery_partner_name}</strong>
                    </div>
                    <div className="order-routing-detail-row">
                      <span>Vehicle Type</span>
                      <strong>{selectedOrderDeliveryPartner?.vehicle_type || 'N/A'}</strong>
                    </div>
                    <div className="order-routing-detail-row">
                      <span>Vehicle Number</span>
                      <strong>{selectedOrderDeliveryPartner?.vehicle_number || 'N/A'}</strong>
                    </div>
                    <div className="order-routing-detail-row">
                      <span>Phone</span>
                      <strong>{selectedOrderDeliveryPartner?.phone || selectedOrder.delivery_partner_phone || 'N/A'}</strong>
                    </div>
                  </div>
                )}

                {selectedOrderReceiver && (
                  <div className="order-routing-detail-section">
                    <div className="order-routing-info-title">Receiver Details</div>
                    <div className="order-routing-detail-row">
                      <span>Name</span>
                      <strong>{selectedOrderReceiver.name || 'N/A'}</strong>
                    </div>
                    <div className="order-routing-detail-row">
                      <span>Phone</span>
                      <strong>{selectedOrderReceiver.phone || 'N/A'}</strong>
                    </div>
                    <div className="order-routing-detail-row">
                      <span>Email</span>
                      <strong>{selectedOrderReceiver.email || 'N/A'}</strong>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Delivery partner dialog - opens only when RIDER_ACCEPTED is picked, never shown inline */}
      {showPartnerDialog && (
        <div className="order-routing-modal-overlay" onClick={() => setShowPartnerDialog(false)}>
          <div className="order-routing-modal order-routing-partner-modal" onClick={(e) => e.stopPropagation()}>
            <div className="order-routing-modal-header">
              <div>
                <h3>Choose a Delivery Partner</h3>
                <div className="order-routing-modal-subtitle">
                  {(selectedOrderVehicleType || 'Bike')} partners
                </div>
              </div>
              <button className="order-routing-modal-close" onClick={() => setShowPartnerDialog(false)}>
                &times;
              </button>
            </div>

            {hasDeliveryPartnerDetails && (
              <div className="order-routing-reassign-reason standalone">
                <label htmlFor="reassign-reason-input">Reason for reassignment</label>
                <textarea
                  id="reassign-reason-input"
                  value={reassignReason}
                  onChange={(e) => setReassignReason(e.target.value)}
                  placeholder="e.g. Rider vehicle breakdown"
                  rows={2}
                />
              </div>
            )}

            <div className="order-routing-modal-body">
              {loadingPartners ? (
                <div className="order-routing-loading">Loading delivery partners...</div>
              ) : partnersError ? (
                <div className="order-routing-error">{partnersError}</div>
              ) : deliveryPartners.length === 0 ? (
                <div className="order-routing-empty">No delivery partners available right now.</div>
              ) : (
                <div className="order-routing-partner-cards">
                  {deliveryPartners.map((partner) => {
                    const isSelected = partner.partner_id === selectedPartnerId;
                    return (
                      <div
                        key={partner.partner_id}
                        className={`order-routing-partner-card ${isSelected ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedPartnerId(partner.partner_id);
                          setShowPartnerDialog(false);
                        }}
                      >
                        <div className="order-routing-partner-avatar">
                          {(partner.name || '?').slice(0, 2).toUpperCase()}
                        </div>
                        <div className="order-routing-partner-info">
                          <div className="order-routing-partner-name">{partner.name}</div>
                          <div className="order-routing-partner-meta">
                            <span className="order-routing-partner-vehicle">
                              {vehicleIcon(partner.vehicle_type)} {partner.vehicle_type || 'N/A'}
                            </span>
                            {partner.vehicle_number ? ` · ${partner.vehicle_number}` : ''}
                            {partner.rating ? (
                              <span className="order-routing-partner-rating">
                                <FaStar /> {Number(partner.rating).toFixed(1)}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <span className={`order-routing-partner-status ${partner.is_available === 1 ? 'active' : 'inactive'}`}>
                          {partner.is_available === 1 ? 'Available' : 'Unavailable'}
                        </span>
                        <input
                          type="radio"
                          checked={isSelected}
                          readOnly
                          className="order-routing-partner-radio"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Vendor details dialog - uses the same vendor data already in the list, no extra API call */}
      {selectedVendor && (
        <div className="order-routing-modal-overlay" onClick={() => setSelectedVendor(null)}>
          <div className="order-routing-modal order-routing-vendor-modal" onClick={(e) => e.stopPropagation()}>
            <div className="order-routing-vendor-modal-header">
              <div className="order-routing-vendor-modal-avatar">
                {(selectedVendor.business_name || '?').slice(0, 2).toUpperCase()}
              </div>
              <div className="order-routing-vendor-modal-title">
                <div className="order-routing-vendor-modal-name-row">
                  <span>{selectedVendor.business_name}</span>
                  <span className={`order-routing-vendor-status ${selectedVendor.status === 1 ? 'active' : 'inactive'}`}>
                    {selectedVendor.status === 1 ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="order-routing-vendor-modal-subtext">
                  {selectedVendor.business_id} &middot; Vendor
                </div>
              </div>
              <button className="order-routing-modal-close" onClick={() => setSelectedVendor(null)}>
                &times;
              </button>
            </div>

            <div className="order-routing-modal-body">
              <div className="order-routing-vendor-section-title">Business Details</div>
              <div className="order-routing-vendor-info-card">
                <div className="order-routing-vendor-info-row">
                  <span className="order-routing-vendor-info-label">
                    <span className="order-routing-vendor-info-icon"><FaBuilding /></span>
                    Business Type
                  </span>
                  <strong>{selectedVendor.business_type || 'N/A'}</strong>
                </div>
                <div className="order-routing-vendor-info-row">
                  <span className="order-routing-vendor-info-label">
                    <span className="order-routing-vendor-info-icon"><FaTags /></span>
                    Category
                  </span>
                  <strong>{selectedVendor.business_category || 'N/A'}</strong>
                </div>
                <div className="order-routing-vendor-info-row">
                  <span className="order-routing-vendor-info-label">
                    <span className="order-routing-vendor-info-icon"><FaCheckCircle /></span>
                    Status
                  </span>
                  <span className={`order-routing-vendor-status ${selectedVendor.status === 1 ? 'active' : 'inactive'}`}>
                    {selectedVendor.status === 1 ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="order-routing-vendor-info-row">
                  <span className="order-routing-vendor-info-label">
                    <span className="order-routing-vendor-info-icon"><FaCalendarAlt /></span>
                    Registered
                  </span>
                  <strong>{formatOrderDate(selectedVendor.created_at)}</strong>
                </div>
              </div>

              <div className="order-routing-vendor-section-title">Contact &amp; Address</div>
              <div className="order-routing-vendor-info-card">
                <div className="order-routing-vendor-info-row">
                  <span className="order-routing-vendor-info-label">
                    <span className="order-routing-vendor-info-icon"><FaEnvelope /></span>
                    Email
                  </span>
                  <strong className="order-routing-vendor-info-link">{selectedVendor.business_email || 'N/A'}</strong>
                </div>
                <div className="order-routing-vendor-info-row">
                  <span className="order-routing-vendor-info-label">
                    <span className="order-routing-vendor-info-icon"><FaPhoneAlt /></span>
                    Phone
                  </span>
                  <strong>{selectedVendor.business_number || 'N/A'}</strong>
                </div>
                <div className="order-routing-vendor-info-row">
                  <span className="order-routing-vendor-info-label">
                    <span className="order-routing-vendor-info-icon whatsapp"><FaWhatsapp /></span>
                    WhatsApp
                  </span>
                  <strong>{selectedVendor.business_whatsapp || 'N/A'}</strong>
                </div>
                <div className="order-routing-vendor-info-row">
                  <span className="order-routing-vendor-info-label">
                    <span className="order-routing-vendor-info-icon"><FaMapMarkerAlt /></span>
                    Address
                  </span>
                  <strong>{selectedVendor.address || 'N/A'}</strong>
                </div>
              </div>

              {vendorDialogStage === 'status' && (
                <>
                  {(() => {
                    const steps = getStatusGridOptions();
                    const currentIndex = steps.findIndex(
                      (s) => String(selectedOrder?.status || '').toLowerCase() === String(s).toLowerCase()
                    );
                    const progressIndex = currentIndex === -1 ? 0 : currentIndex;

                    return (
                      <>
                        <div className="order-routing-vendor-section-title-row">
                          <span>Order Status</span>
                          <span className="order-routing-stepper-count">
                            Step {progressIndex + 1} of {steps.length}
                          </span>
                        </div>
                        {/* All steps visible at once: 5-column grid, wraps to a second row, no scrolling */}
                        <div className="order-routing-stepper-track">
                          <div className="order-routing-stepper-progress">
                            <span style={{ width: `${((progressIndex + 1) / steps.length) * 100}%` }} />
                          </div>
                          <div className="order-routing-stepper">
                            {steps.map((status, index) => {
                              const isCurrent = String(selectedOrder?.status || '').toLowerCase() === String(status).toLowerCase();
                              const isPast = currentIndex !== -1 && index < currentIndex;
                              // Only the very next status can be picked - can't skip
                              // ahead past it, and can't go back to an earlier one.
                              const isSkippedAhead = currentIndex !== -1 && index > currentIndex + 1;
                              const isSelected = selectedVendorStatus === status;
                              return (
                                <button
                                  key={status}
                                  type="button"
                                  className={`order-routing-stepper-step ${isCurrent ? 'current' : ''} ${isPast ? 'completed' : ''} ${isSelected && !isCurrent ? 'selected' : ''}`}
                                  disabled={isAssignmentStatus(status) ? false : (isCurrent || isPast || isSkippedAhead)}
                                  onClick={() => {
                                    setSelectedVendorStatus(status);
                                    if (!isAssignmentStatus(status)) setSelectedVendorPartnerId(null);
                                  }}
                                >
                                  <span className="order-routing-stepper-circle">
                                    {isPast ? <FaCheck /> : stepIcon(status)}
                                  </span>
                                  <span className="order-routing-stepper-label">{formatStatusLabel(status)}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    );
                  })()}

                  {/* The vendor's delivery partners only show up once RIDER_ACCEPTED is picked above */}
                  {isAssignmentStatus(selectedVendorStatus) && (
                    <>
                      <div className="order-routing-vendor-section-title-row">
                        <span>Delivery Partners</span>
                        {vendorPartners.length > 0 && (
                          <span className="order-routing-vendors-count">
                            {vendorPartners.filter((p) => p.is_available === 1).length} available
                          </span>
                        )}
                      </div>
                      {hasDeliveryPartnerDetails && (
                        <div className="order-routing-reassign-reason">
                          <label htmlFor="vendor-reassign-reason-input">Reason for reassignment</label>
                          <textarea
                            id="vendor-reassign-reason-input"
                            value={reassignReason}
                            onChange={(e) => setReassignReason(e.target.value)}
                            placeholder="e.g. Rider vehicle breakdown"
                            rows={2}
                          />
                        </div>
                      )}
                      {loadingVendorPartners ? (
                        <div className="order-routing-loading">Loading delivery partners...</div>
                      ) : vendorPartnersError ? (
                        <div className="order-routing-error">{vendorPartnersError}</div>
                      ) : vendorPartners.length === 0 ? (
                        <div className="order-routing-empty">No delivery partners found for this vendor.</div>
                      ) : (
                        <div className="order-routing-vendor-partner-cards">
                          {vendorPartners.map((partner, index) => {
                            const isSelected = partner.partner_id === selectedVendorPartnerId;
                            return (
                              <div
                                key={partner.partner_id}
                                className={`order-routing-vendor-partner-card ${isSelected ? 'selected' : ''}`}
                                onClick={() => setSelectedVendorPartnerId(partner.partner_id)}
                              >
                                <div className={`order-routing-vendor-partner-avatar avatar-${index % 3}`}>
                                  {(partner.name || '?').slice(0, 2).toUpperCase()}
                                </div>
                                <div className="order-routing-partner-info">
                                  <div className="order-routing-partner-name">{partner.name}</div>
                                  <div className="order-routing-partner-meta">
                                    <span className="order-routing-vendor-partner-tag">
                                      {vehicleIcon(partner.vehicle_type)} {partner.vehicle_type || 'N/A'}
                                      {partner.vehicle_number ? ` · ${partner.vehicle_number}` : ''}
                                    </span>
                                    {partner.rating ? (
                                      <span className="order-routing-partner-rating">
                                        <FaStar /> {Number(partner.rating).toFixed(1)}
                                      </span>
                                    ) : null}
                                  </div>
                                </div>
                                <span className={`order-routing-vendor-partner-status ${partner.is_available === 1 ? 'active' : 'inactive'}`}>
                                  <span className="dot" /> {partner.is_available === 1 ? 'Available' : 'On trip'}
                                </span>
                                <input
                                  type="radio"
                                  checked={isSelected}
                                  readOnly
                                  className="order-routing-vendor-partner-radio"
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            {vendorDialogStage === 'confirm' ? (
              <div className="order-routing-vendor-modal-footer">
                {vendorAssignError && <div className="order-routing-error">{vendorAssignError}</div>}
                <div className="order-routing-vendor-confirm-hint">
                  Assign <strong>{selectedVendor.business_name}</strong> to this order.
                </div>
                <div className="order-routing-vendor-modal-actions">
                  <button className="order-routing-vendor-back-btn" onClick={() => setSelectedVendor(null)}>
                    Back
                  </button>
                  <button
                    className="order-routing-assign-order-btn"
                    disabled={isAssigningVendorOrder}
                    onClick={handleConfirmVendorAssignment}
                  >
                    {isAssigningVendorOrder ? 'Assigning...' : 'Assign Vendor'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="order-routing-vendor-modal-footer">
                {vendorAssignError && <div className="order-routing-error">{vendorAssignError}</div>}
                <span className="order-routing-footer-hint">
                  {selectedVendorPartner ? (
                    <>Assigning to <strong>{selectedVendorPartner.name}</strong> &middot; {selectedVendorPartner.vehicle_type}</>
                  ) : !selectedVendorStatus ? (
                    'Select a status to continue'
                  ) : isAssignmentStatus(selectedVendorStatus) ? (
                    'Select a delivery partner to continue'
                  ) : (
                    ''
                  )}
                </span>
                <div className="order-routing-vendor-modal-actions">
                  <button className="order-routing-vendor-back-btn" onClick={() => setSelectedVendor(null)}>
                    Back
                  </button>
                  <button
                    className="order-routing-assign-order-btn"
                    disabled={
                      isAssigningVendorOrder ||
                      !selectedVendorStatus ||
                      (isAssignmentStatus(selectedVendorStatus) && !selectedVendorPartnerId)
                    }
                    onClick={handleAssignToVendor}
                  >
                    {isAssigningVendorOrder
                      ? 'Updating...'
                      : isAssignmentStatus(selectedVendorStatus)
                        ? 'Assign Order'
                        : 'Update Status'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderRouting;
